import type { Recipe } from '../types/recipe';

/**
 * Returns the canonical, Firestore-resolvable id to persist on a meal.
 *
 * Imported recipes carry a one-way hashed-numeric `id` (>= 9000000) produced by
 * `hashRecipeId`, which cannot be reversed to look the recipe back up. But every
 * such recipe ALSO carries `_firestoreId` — its real Firestore document id
 * (`saved-…`/`imported-…`). Preferring `_firestoreId` guarantees we never persist
 * a non-resolvable hash, so rehydration can always find the recipe again.
 *
 * - imported/hashed recipe   → `_firestoreId` (the resolvable doc id)
 * - already-canonical string → returned as-is (`saved-…`/`imported-…`/personal uuid)
 * - catalog numeric id       → `String(id)` (Spoonacular; resolves via the catalog)
 */
export function getPersistableRecipeId(recipe: Pick<Recipe, 'id' | '_firestoreId'>): string {
  if (recipe._firestoreId) return recipe._firestoreId;
  if (typeof recipe.id === 'string') return recipe.id;
  return String(recipe.id);
}

/**
 * Classify a persisted recipeId for observability. A `hashed_numeric` id is the
 * legacy artifact this stage eliminates: it is one-way and unresolvable on the
 * client, so seeing it in `recipe_rehydrate_failed` means a meal predates the
 * canonicalization fix and still needs the server-side backfill.
 *
 * A `catalog` id is a shared `recipes/` document that was created there rather
 * than promoted from a cook's library: a bare UUID, or a bulk import's
 * publisher prefix. Those are every non-`saved-`/`imported-` id in the
 * catalogue (#777 — a deleted UUID read as `unknown`).
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CATALOG_PREFIXES = ['katrine-', 'nyt-', 'troisfois-'];

export function classifyRecipeId(
  recipeId: string
): 'hashed_numeric' | 'saved' | 'imported' | 'catalog' | 'unknown' {
  if (Number(recipeId) >= 9000000) return 'hashed_numeric';
  if (recipeId.startsWith('saved-')) return 'saved';
  if (recipeId.startsWith('imported-')) return 'imported';
  if (UUID_RE.test(recipeId) || CATALOG_PREFIXES.some((p) => recipeId.startsWith(p))) return 'catalog';
  return 'unknown';
}
